import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import auth from "../../../firebase.init";

// Configuration
const API_ROOT = process.env.REACT_APP_API_URL || "https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/";
const MAX_WORDS = 800;

// --- Options Constants ---
const PLATFORM_OPTIONS = [
  "YouTube Video", "YouTube Short", "Instagram Reel", "TikTok",
  "Facebook Video", "Video Ad", "Explainer Video",
];

const STYLE_OPTIONS = [
  "Educational / How-to", "Storytelling / Narrative", "Promotional / Sales",
  "Conversational / Vlog", "Professional / Corporate", "Funny / Entertaining",
];

// NEW: Word Limit Options replacing Duration
const WORD_LIMIT_OPTIONS = [
  "150 Words (Shorts/Reels)",
  "300 Words (1 Min)",
  "500 Words (3-5 Mins)",
  "800 Words (Long Form)",
  "1000 Words (In Depth)",
];

const VOICE_OPTIONS = [
  { label: "Achernar — Warm", value: "achernar" },
  { label: "Achird — Calm", value: "achird" },
  { label: "Algenib — Deep", value: "algenib" },
  { label: "Algieba — Smooth", value: "algieba" },
  { label: "Alnilam — Clear", value: "alnilam" },
  { label: "Aoede — Light", value: "aoede" },
  { label: "Autonoe — Soft", value: "autonoe" },
  { label: "Callirrhoe — Balanced", value: "callirrhoe" },
  { label: "Charon — Informative", value: "charon" },
  { label: "Despina — Strong", value: "despina" },
  { label: "Enceladus — Neutral", value: "enceladus" },
  { label: "Erinome — Gentle", value: "erinome" },
  { label: "Fenrir — Excitable", value: "fenrir" },
  { label: "Gacrux — Bright", value: "gacrux" },
  { label: "Iapetus — Natural", value: "iapetus" },
  { label: "Kore — Firm", value: "kore" },
  { label: "Laomedeia — Soft", value: "laomedeia" },
];

// --- Helper Functions ---
const pcmToWav = (pcmData, sampleRate, numChannels = 1, bitsPerSample = 16) => {
  const byteRate = (sampleRate * numChannels * bitsPerSample) / 8;
  const blockAlign = (numChannels * bitsPerSample) / 8;
  const buffer = new ArrayBuffer(44 + pcmData.byteLength);
  const view = new DataView(buffer);

  function writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  let offset = 0;
  writeString(view, offset, "RIFF");
  offset += 4;
  view.setUint32(offset, 36 + pcmData.byteLength, true);
  offset += 4;
  writeString(view, offset, "WAVE");
  offset += 4;
  writeString(view, offset, "fmt ");
  offset += 4;
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, numChannels, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;
  view.setUint32(offset, byteRate, true);
  offset += 4;
  view.setUint16(offset, blockAlign, true);
  offset += 2;
  view.setUint16(offset, bitsPerSample, true);
  offset += 2;
  writeString(view, offset, "data");
  offset += 4;
  view.setUint32(offset, pcmData.byteLength, true);
  offset += 4;

  for (let i = 0; i < pcmData.byteLength; i++) {
    view.setUint8(offset + i, pcmData[i]);
  }

  return new Blob([buffer], { type: "audio/wav" });
};

const VideoScriptGenerator = () => {
  const navigate = useNavigate();
  const [user, loadingAuth] = useAuthState(auth);

  // Loading States
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [generatingVoice, setGeneratingVoice] = useState(false);

  // Data States
  const [apiKeys, setApiKeys] = useState([]);
  const [credits, setCredits] = useState(0);

  // Form State
  const [scriptParams, setScriptParams] = useState({
    topic: "",
    platform: "YouTube Video",
    style: "Educational / How-to",
    wordLimit: "500 Words (3-5 Mins)", // Replaced duration with wordLimit
    audience: "",
    tone: "",
    cta: "",
  });

  const [generatedScript, setGeneratedScript] = useState("");
  const [generatedAudioMeta, setGeneratedAudioMeta] = useState(null);
  const [speaker1Voice, setSpeaker1Voice] = useState("achernar");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // --- API & Auth Init ---
  const fetchApiKeys = async () => {
    try {
      const resp = await axios.get(`${API_ROOT}/api/gemini-keys`);
      if (resp.data?.success && Array.isArray(resp.data.data)) {
        const keys = resp.data.data.map((k) => k.key).filter(Boolean);
        setApiKeys(keys);
        if (!keys.length) setError("No API keys available. Contact admin.");
      } else setError("Failed to load API keys");
    } catch (err) {
      console.error("fetchApiKeys err:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  const getRandomApiKey = () => {
    if (!apiKeys.length) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  useEffect(() => {
    const init = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      setError("");
      setSuccess("");
      try {
        await fetchApiKeys();
        const uResp = await axios.get(`${API_ROOT}/users`, {
          params: { userEmail: user.email },
        });
        if (uResp.data?.length > 0)
          setCredits(uResp.data[0].currentBalance ?? 0);
        else setError("User data not found");
      } catch (err) {
        console.error("init err:", err);
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user, loadingAuth]);

  // --- Credit Logic ---
  const deductCredits = async (amount) => {
    const resp = await axios.post(`${API_ROOT}/api/deduct-credit`, {
      userEmail: user.email,
      amount,
    });
    if (!resp.data?.success) throw new Error("Failed to deduct credits");
    setCredits((p) => p - amount);
    return true;
  };

  const refundCredits = async (amount) => {
    try {
      const resp = await axios.post(`${API_ROOT}/api/refund-credit`, {
        userEmail: user.email,
        amount,
      });
      if (resp.data?.success) setCredits((p) => p + amount);
    } catch (err) {
      console.error("refundCredits err:", err);
    }
  };

  // --- Input Handler ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setScriptParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // --- Generation Logic ---
  const generateScript = async () => {
    setError("");
    setSuccess("");

    if (!user) return setError("Please login to generate scripts");
    if (!scriptParams.topic.trim()) return setError("Video Topic is required");
    if (credits < 1) return setError("Insufficient credits. Need 1 credit");
    if (!apiKeys.length) return setError("No API keys available");

    setGeneratingScript(true);
    setGeneratedScript("");
    setGeneratedAudioMeta(null);

    try {
      await deductCredits(1);
    } catch (err) {
      setGeneratingScript(false);
      return setError("Failed to deduct credit: " + err.message);
    }

    try {
      const MODEL_ID = "gemini-2.0-flash";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;

      const fullPrompt = `
        Act as a professional video scriptwriter. Create a script based on these details:
        
        - Platform: ${scriptParams.platform}
        - Topic: "${scriptParams.topic}"
        - Style: ${scriptParams.style}
        - Target Audience: ${scriptParams.audience || "General"}
        - Word Limit: ${scriptParams.wordLimit} (Strictly adhere to this word count)
        - Tone of Voice: ${scriptParams.tone || "Engaging"}
        - Call to Action (CTA): ${scriptParams.cta || "Subscribe/Follow"}

        Required Structure:
        1. **Hook**: Grab attention immediately.
        2. **Intro**: Briefly introduce the topic.
        3. **Main Content**: The core message/value.
        4. **CTA / Outro**: Clear direction for the viewer.

        Format requirements:
        - Output ONLY the script text (and speaker directions in parentheses if needed).
        - Make it sound natural and spoken, not robotic.
        - Do not include introductory conversational filler.
      `;

      const requestBody = {
        contents: [{ role: "user", parts: [{ text: fullPrompt }] }],
        generationConfig: { maxOutputTokens: 1500 },
      };

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API Error: ${res.status} - ${text}`);
      }

      const data = await res.json();
      let scriptText = data?.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!scriptText) throw new Error("No script returned from API");

      scriptText = scriptText.replace(/```/g, "").trim();
      const words = scriptText.trim().split(/\s+/);
      if (words.length > MAX_WORDS) {
        scriptText = words.slice(0, MAX_WORDS).join(" ");
      }

      setGeneratedScript(scriptText);
      setSuccess("Script generated successfully!");

    } catch (err) {
      console.error(err);
      await refundCredits(1);
      setError("Script generation failed: " + (err.message || err));
    } finally {
      setGeneratingScript(false);
    }
  };

  const generateVoiceFromScript = async () => {
    setError("");
    setSuccess("");
    if (!user) return setError("Please login to generate voice");

    const scriptToUse = generatedScript || scriptParams.topic;
    if (!scriptToUse) return setError("Generate a script first.");

    const creditsNeeded = 1;
    if (credits < creditsNeeded)
      return setError(`Insufficient credits. Need ${creditsNeeded} credit(s)`);

    setGeneratingVoice(true);
    let creditsDeducted = 0;

    try {
      await deductCredits(creditsNeeded);
      creditsDeducted = creditsNeeded;
    } catch (err) {
      setGeneratingVoice(false);
      return setError("Failed to deduct credit: " + err.message);
    }

    try {
      const apiKey = getRandomApiKey();
      const ttsApiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-tts:generateContent?key=${apiKey}`;

      const cleanScript = scriptToUse.replace(/[*#]/g, "");

      const payload = {
        contents: [
          {
            role: "user",
            parts: [{ text: cleanScript }],
          },
        ],
        generationConfig: {
          responseModalities: ["audio"],
          speech_config: {
            voice_config: {
              prebuilt_voice_config: { voice_name: speaker1Voice },
            },
          },
        },
      };

      const response = await fetch(ttsApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Gemini TTS API Error: ${response.status} - ${text}`);
      }

      const data = await response.json();
      const audioPart = data?.candidates?.[0]?.content?.parts?.[0];
      if (!audioPart?.inlineData?.data)
        throw new Error("No audio returned from TTS API");

      const base64Audio = audioPart.inlineData.data;
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const wavBlob = pcmToWav(bytes, 24000);
      const blobUrl = URL.createObjectURL(wavBlob);

      setGeneratedAudioMeta({
        blob: wavBlob,
        blobUrl,
        filename: `script-audio-${Date.now()}.wav`,
        speakers: [speaker1Voice],
      });

      setSuccess("Voice generated successfully!");
    } catch (err) {
      console.error(err);
      await refundCredits(creditsDeducted);
      setError("Voice generation failed: " + (err.message || err));
    } finally {
      setGeneratingVoice(false);
    }
  };

  const downloadAudio = () => {
    if (!generatedAudioMeta) return;
    const link = document.createElement("a");
    link.href = generatedAudioMeta.blobUrl;
    link.download = generatedAudioMeta.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(generatedScript);
    setSuccess("Script copied to clipboard!");
    setTimeout(() => setSuccess(""), 3000);
  };

  const resetAll = () => {
    setScriptParams({
      topic: "",
      platform: "YouTube Video",
      style: "Educational / How-to",
      wordLimit: "500 Words (3-5 Mins)", // Reset to default
      audience: "",
      tone: "",
      cta: "",
    });
    setGeneratedScript("");
    setGeneratedAudioMeta(null);
    setSpeaker1Voice("achernar");
    setError("");
    setSuccess("");
  };

  if (loading || loadingKeys)
    return (
      <div className="d-flex justify-content-center align-items-center bg-dark" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  return (
    <div className="script-generator-wrapper">
      <div className="container py-5">
        
        {/* --- Header Section --- */}
        <div className="row justify-content-center mb-5">
          <div className="col-12 col-lg-10 text-center">
            <h1 className="display-4 fw-bold text-gradient mb-2">
              AI Video Script Generator
            </h1>
            <p className="text-secondary lead mb-4">
              Turn ideas into production-ready scripts for YouTube, TikTok & Ads.
            </p>

            {/* Credits Badge */}
            <div className="d-inline-flex align-items-center bg-dark-glass px-4 py-2 rounded-pill shadow-sm mb-3 border border-secondary border-opacity-25">
              <span className="text-light me-2">Available Credits:</span>
              <span className={`badge ${credits > 0 ? 'bg-warning text-dark' : 'bg-danger'} fs-6 rounded-pill`}>
                {credits}
              </span>
            </div>

            {/* Buy Button */}
            {credits < 5 && (
              <div className="mt-2">
                <button
                  className="btn btn-sm btn-outline-warning rounded-pill px-3"
                  onClick={() => navigate("/user/select-package")}
                >
                  <i className="bi bi-plus-circle me-1"></i> Buy Credits
                </button>
              </div>
            )}

            {/* Notifications */}
            {error && (
              <div className="alert alert-danger mt-3 mx-auto w-75 shadow-sm border-0 d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2"></i> {error}
              </div>
            )}
            {success && (
              <div className="alert alert-success mt-3 mx-auto w-75 shadow-sm border-0 d-flex align-items-center">
                 <i className="bi bi-check-circle-fill me-2"></i> {success}
              </div>
            )}
          </div>
        </div>

        {/* --- Main Content Grid --- */}
        <div className="row g-4">
          
          {/* LEFT COL: Inputs */}
          <div className="col-12 col-lg-5">
            <div className="card h-100 bg-glass shadow-lg border-0">
              <div className="card-body p-4">
                <h4 className="text-white mb-4 fw-bold border-bottom border-white border-opacity-10 pb-3">
                  <i className="bi bi-sliders me-2 text-primary"></i>Script Details
                </h4>

                <div className="row g-3">
                  {/* Platform */}
                  <div className="col-md-6">
                    <label className="form-label text-light small fw-bold text-uppercase opacity-75">Platform</label>
                    <select 
                      className="form-select bg-dark-input text-white border-0 shadow-none"
                      name="platform"
                      value={scriptParams.platform}
                      onChange={handleInputChange}
                    >
                      {PLATFORM_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  {/* Style */}
                  <div className="col-md-6">
                    <label className="form-label text-light small fw-bold text-uppercase opacity-75">Video Style</label>
                    <select 
                      className="form-select bg-dark-input text-white border-0 shadow-none"
                      name="style"
                      value={scriptParams.style}
                      onChange={handleInputChange}
                    >
                      {STYLE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  {/* Topic (Full Width) */}
                  <div className="col-12">
                    <label className="form-label text-light small fw-bold text-uppercase opacity-75">Video Topic / Title</label>
                    <input 
                      type="text"
                      className="form-control bg-dark-input text-white border-0 shadow-none py-2"
                      placeholder="e.g., How to bake sourdough bread for beginners"
                      name="topic"
                      value={scriptParams.topic}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Word Limit (Replaces Duration) */}
                  <div className="col-md-6">
                    <label className="form-label text-light small fw-bold text-uppercase opacity-75">Word Limit</label>
                    <select 
                      className="form-select bg-dark-input text-white border-0 shadow-none"
                      name="wordLimit"
                      value={scriptParams.wordLimit}
                      onChange={handleInputChange}
                    >
                      {WORD_LIMIT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                   {/* Tone */}
                   <div className="col-md-6">
                    <label className="form-label text-light small fw-bold text-uppercase opacity-75">Tone</label>
                    <input 
                      type="text"
                      className="form-control bg-dark-input text-white border-0 shadow-none"
                      placeholder="e.g. Enthusiastic"
                      name="tone"
                      value={scriptParams.tone}
                      onChange={handleInputChange}
                    />
                  </div>

                   {/* Audience */}
                   <div className="col-md-6">
                    <label className="form-label text-light small fw-bold text-uppercase opacity-75">Target Audience</label>
                    <input 
                      type="text"
                      className="form-control bg-dark-input text-white border-0 shadow-none"
                      placeholder="e.g. Home cooks"
                      name="audience"
                      value={scriptParams.audience}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* CTA */}
                  <div className="col-md-6">
                    <label className="form-label text-light small fw-bold text-uppercase opacity-75">Call to Action</label>
                    <input 
                      type="text"
                      className="form-control bg-dark-input text-white border-0 shadow-none"
                      placeholder="e.g. Subscribe"
                      name="cta"
                      value={scriptParams.cta}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <hr className="border-secondary my-4 opacity-25" />

                {/* Action Buttons */}
                <div className="d-grid gap-3">
                  <button 
                    className="btn btn-primary btn-lg fw-bold shadow-lg glow-on-hover border-0 text-white"
                    onClick={generateScript}
                    disabled={generatingScript || generatingVoice}
                    style={{background: 'linear-gradient(45deg, #0d6efd, #6610f2)'}}
                  >
                    {generatingScript ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Writing Script...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-stars me-2"></i>Generate Script (1 Credit)
                      </>
                    )}
                  </button>
                  <button 
                    className="btn btn-outline-light btn-sm opacity-50 hover-opacity-100"
                    onClick={resetAll}
                    disabled={generatingScript}
                  >
                    Reset Form
                  </button>
                </div>

              </div>
            </div>
          </div>

          {/* RIGHT COL: Output */}
          <div className="col-12 col-lg-7">
            <div className="card h-100 bg-glass shadow-lg border-0 position-relative overflow-hidden">
              <div className="card-header bg-transparent border-bottom border-white border-opacity-10 d-flex justify-content-between align-items-center py-3">
                <h5 className="text-white mb-0 fw-bold">
                  <i className="bi bi-file-text me-2 text-primary"></i>Generated Script
                </h5>
                {generatedScript && !generatingScript && (
                  <button className="btn btn-sm btn-dark bg-dark-input border-0 text-light hover-scale" onClick={handleCopyScript}>
                    <i className="bi bi-clipboard me-1"></i> Copy
                  </button>
                )}
              </div>
              
              <div className="card-body d-flex flex-column p-0 custom-scrollbar-container">
                {generatingScript ? (
                   <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5 text-center">
                     <div className="mb-4">
                        <div className="spinner-grow text-primary" role="status" style={{width: '3rem', height: '3rem'}}></div>
                     </div>
                     <p className="text-light fs-5 animate-pulse">AI is crafting your story...</p>
                   </div>
                ) : (
                  <textarea
                    className="form-control bg-transparent text-white border-0 h-100 p-4 custom-scrollbar"
                    style={{ resize: "none", minHeight: "500px", fontSize: "1.05rem", lineHeight: "1.7" }}
                    value={generatedScript}
                    readOnly
                    placeholder="Your generated script will appear here ready for production."
                  ></textarea>
                )}
              </div>

              {/* Footer: Voice Generation Integration */}
              {/* <div className="card-footer bg-dark-glass border-top border-white border-opacity-10 p-3">
                <div className="row align-items-end g-2">
                  <div className="col-md-7">
                     <label className="text-white-50 small mb-1 fw-bold text-uppercase">AI Voiceover (Optional)</label>
                     <select
                       className="form-select form-select-sm bg-dark text-white border-secondary border-opacity-25"
                       value={speaker1Voice}
                       onChange={(e) => setSpeaker1Voice(e.target.value)}
                     >
                       {VOICE_OPTIONS.map((v) => (
                         <option key={v.value} value={v.value}>{v.label}</option>
                       ))}
                     </select>
                  </div>
                  <div className="col-md-5 d-grid">
                    {!generatedAudioMeta ? (
                      <button 
                        className="btn btn-success btn-sm fw-bold border-0"
                        onClick={generateVoiceFromScript}
                        disabled={!generatedScript || generatingVoice || generatingScript}
                        style={{background: 'linear-gradient(45deg, #198754, #20c997)'}}
                      >
                        {generatingVoice ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Generating...
                            </>
                        ) : "Generate Voice (1 Credit)"}
                      </button>
                    ) : (
                      <div className="d-flex gap-2 align-items-center bg-dark rounded p-1 border border-secondary border-opacity-25">
                        <audio controls src={generatedAudioMeta.blobUrl} className="w-100" style={{height:'30px'}} />
                        <button className="btn btn-link text-white p-0 me-2" onClick={downloadAudio} title="Download">
                          <i className="bi bi-download fs-5"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div> */}

            </div>
          </div>

        </div>
      </div>

      {/* --- INLINE STYLES FOR GLASSMORPHISM & ANIMATION --- */}
      <style>{`
        .script-generator-wrapper {
          min-height: 100vh;
          background: #0f172a; /* Fallback */
          background: radial-gradient(circle at 10% 20%, rgb(15, 23, 42) 0%, rgb(22, 30, 50) 90%);
          color: #fff;
        }

        /* Glassmorphism Cards */
        .bg-glass {
          background: rgba(30, 41, 59, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .bg-dark-glass {
            background: rgba(15, 23, 42, 0.6);
        }

        /* Inputs */
        .bg-dark-input {
          background: rgba(15, 23, 42, 0.6);
          color: #e2e8f0;
          transition: all 0.3s ease;
        }
        
        .form-control:focus, .form-select:focus {
          background: rgba(15, 23, 42, 0.9);
          color: #fff;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.5); /* Indigo glow */
          border-color: transparent;
        }

        .text-gradient {
          background: linear-gradient(to right, #6366f1, #a855f7, #ec4899);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .glow-on-hover:hover {
          box-shadow: 0 0 20px rgba(99, 102, 241, 0.6) !important;
          transform: translateY(-1px);
        }

        .hover-scale:hover {
            transform: scale(1.05);
            transition: transform 0.2s;
        }

        .hover-opacity-100:hover {
            opacity: 1 !important;
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .5; }
        }

        /* Custom Scrollbar for Textarea */
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1); 
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2); 
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.4); 
        }
        
        /* Audio Player Styling Override (Webkit) */
        audio::-webkit-media-controls-panel {
            background-color: #cbd5e1;
        }
      `}</style>
    </div>
  );
};

export default VideoScriptGenerator;